import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import {
  Sport,
  SubscribableType,
} from '@sorare/core/src/__generated__/globalTypes';
import { LoadMoreButton } from '@sorare/core/src/atoms/buttons/LoadMoreButton';
import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { LoadingIndicator } from '@sorare/core/src/atoms/loader/LoadingIndicator';
import { BodyM } from '@sorare/core/src/atoms/typography';
import { extractConnectionData } from '@sorare/core/src/gql/extractData';
import { usePaginatedQuery } from '@sorare/core/src/hooks/graphql/usePaginatedQuery';

import { MyPage } from '#src/components/mySorare/MyPage/index.tsx';
import { MySorareSubTabsLayout } from '#src/components/mySorare/MySorareSubTabsLayout/index.tsx';
import { useSortByDate } from '#src/components/mySorare/common/useSortByDate.tsx';
import { useSportSelect } from '#src/components/mySorare/common/useSportSelect.tsx';

import { PlayerNotifications } from './PlayerNotifications/index.tsx';
import type {
  FollowsForNotificationsQuery,
  FollowsForNotificationsQueryVariables,
} from './__generated__/index.graphql.ts';

const FOLLOWS_FOR_NOTIFICATIONS_QUERY = gql`
  query FollowsForNotificationsQuery(
    $cursor: String
    $subscriptionsType: [SubscribableType!]
    $sortType: SortingOption
  ) {
    currentUser {
      slug
      mySubscriptions(
        types: $subscriptionsType
        after: $cursor
        sortType: $sortType
      ) {
        nodes {
          slug
          updatedAt
          ...PlayerNotifications_subscription
        }
        pageInfo {
          endCursor
          hasNextPage
        }
      }
    }
  }
  ${PlayerNotifications.fragments.subscription}
` as TypedDocumentNode<
  FollowsForNotificationsQuery,
  FollowsForNotificationsQueryVariables
>;

const playersSubscriptionsType = {
  [Sport.BASEBALL]: SubscribableType.BASEBALL_PLAYER,
  [Sport.FOOTBALL]: SubscribableType.PLAYER,
  [Sport.NBA]: SubscribableType.NBA_PLAYER,
};

const Root = styled(Vertical)`
  padding: var(--double-unit) 0;
`;

export const PlayersNotifications = () => {
  const { sortAsVariable, sortSelectNode } = useSortByDate();
  const { sportInputValue, sportSelectNode } = useSportSelect();

  const subscriptionsTypeVaribales = useMemo(
    () => sportInputValue.map(sport => playersSubscriptionsType[sport]),
    [sportInputValue]
  );

  const { data, loading, loadMore } = usePaginatedQuery(
    FOLLOWS_FOR_NOTIFICATIONS_QUERY,
    {
      variables: {
        subscriptionsType: subscriptionsTypeVaribales,
        sortType: sortAsVariable,
      },
      connection: 'EmailSubscriptionConnection',
    }
  );

  const {
    items: follows,
    hasMore,
    cursor,
  } = extractConnectionData(data?.currentUser?.mySubscriptions, src => src);

  if (loading || !follows) return <LoadingIndicator />;

  return (
    <MySorareSubTabsLayout tabs={[]}>
      <MyPage
        toolbar={
          <>
            {sportSelectNode}
            {sortSelectNode}
          </>
        }
      >
        <Root>
          {follows.length === 0 ? (
            <BodyM>
              <FormattedMessage
                id="PlayersNotifications.empty"
                defaultMessage="You don't follow anyone yet!"
              />
            </BodyM>
          ) : (
            <Vertical>
              {follows.map(f => (
                <PlayerNotifications key={f.slug} subscription={f} />
              ))}
            </Vertical>
          )}

          <LoadMoreButton
            hasMore={hasMore}
            loading={loading}
            loadMore={() => {
              loadMore(false, {
                cursor,
                sortType: sortAsVariable,
              });
            }}
          />
        </Root>
      </MyPage>
    </MySorareSubTabsLayout>
  );
};
